import { Button, Dropdown, Tooltip } from 'antd';
import {
  FileTextOutlined,
  MoreOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  IoCreateOutline,
  IoDownloadOutline,
  IoEyeOutline,
} from 'react-icons/io5';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ReportListItem } from '@/models/report';
import { useLazyExportReportQuery } from '@/state/slices/studies';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import { AddendumModal } from '@/features/report';
import { StudyInfoModal } from './StudyInfoModal';
import useReportRowModals from './hooks/useReportRowModals';

interface ReportRowActionsProps {
  record: ReportListItem;
}

export default function ReportRowActions({ record }: ReportRowActionsProps) {
  const navigate = useNavigate();
  const [triggerExport] = useLazyExportReportQuery();
  const {
    activeModal,
    study,
    studyLoading,
    addendumLoading,
    openStudyInfo,
    openAddendum,
    closeModal,
    handleAddendumSubmit,
  } = useReportRowModals(record.studyUuid);

  const handleOpenReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/reports/${record.studyUuid}/preview`);
  };

  const handleDownloadPdf = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await triggerExport({ studyUuid: record.studyUuid });
    if (result.data) {
      downloadBlob(result.data, `report-${record.studyUuid}.pdf`);
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'open-reader',
      icon: <IoEyeOutline />,
      label: 'Open Reader',
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        navigate(`/studies/${record.studyUuid}`);
      },
    },
    {
      key: 'view-study-info',
      icon: <IoMdInformationCircleOutline />,
      label: 'View Study Info',
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        openStudyInfo();
      },
    },
    {
      key: 'send-report',
      icon: <SendOutlined />,
      label: (
        <Tooltip title="Coming soon">
          <span>Send Report</span>
        </Tooltip>
      ),
      disabled: true,
    },
    {
      key: 'create-addendum',
      icon: <IoCreateOutline />,
      label: 'Create Addendum',
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        openAddendum();
      },
    },
  ];

  return (
    <>
      <div
        role="toolbar"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Tooltip title="Open report">
          <Button
            type="text"
            icon={<FileTextOutlined />}
            onClick={handleOpenReport}
          />
        </Tooltip>
        <Tooltip title="Download PDF">
          <Button
            type="text"
            aria-label="download"
            icon={<IoDownloadOutline />}
            onClick={handleDownloadPdf}
          />
        </Tooltip>
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </div>

      <StudyInfoModal
        open={activeModal === 'studyInfo'}
        onClose={closeModal}
        study={study}
        loading={studyLoading}
      />

      {study && (
        <AddendumModal
          report={null}
          open={activeModal === 'addendum'}
          onCancel={closeModal}
          onOk={handleAddendumSubmit}
          study={study}
          previousAddendums={[]}
          loading={addendumLoading || studyLoading}
        />
      )}
    </>
  );
}
